import React, { useEffect, useRef, useState } from 'react';
import ValidationGroup from 'devextreme-react/validation-group';
import { ButtonTheme, GenericPopup } from '@apply/components';
import { StepErrorBanner } from './StepErrorBanner';

interface FormModalProps {
    id: string;
    isOpen: boolean;
    title: string;
    onSave: () => Promise<void> | void;
    onClose: () => void;
    children: React.ReactNode;
    saveLabel?: string;
    width?: string;
}

// Add/Edit popup shared by every list step
export const FormModal: React.FC<FormModalProps> = ({
    id,
    isOpen,
    title,
    onSave,
    onClose,
    children,
    saveLabel = 'Save',
    width = '560px',
}) => {
    const validationGroupRef = useRef<ValidationGroup>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState<unknown>(null);

    // A failure from a previous attempt should not remain for the next one
    useEffect(() => {
        if (isOpen) {
            setSaveError(null);
        }
    }, [isOpen]);

    const handleSave = async () => {
        const result = validationGroupRef.current?.instance?.validate();
        if (result && result.isValid === false) {
            return;
        }

        setIsSaving(true);
        setSaveError(null);
        try {
            await onSave();
            onClose();
        } catch (error) {
            // Stay open with the entered values intact so the user can retry
            setSaveError(error);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <GenericPopup
            id={id}
            isOpen={isOpen}
            title={title}
            showTitle={true}
            showCloseButton={true}
            handleClose={onClose}
            width={width}
            maxHeight='90vh'
            actionButtons={[
                {
                    id: `${id}Save`,
                    children: isSaving ? 'Saving…' : saveLabel,
                    theme: ButtonTheme.Primary,
                    disabled: isSaving,
                    handleClick: handleSave,
                    width: '180px',
                },
            ]}
        >
            <ValidationGroup ref={validationGroupRef}>
                <div className='bp-formModal'>
                    {saveError != null && <StepErrorBanner error={saveError} />}
                    {children}
                </div>
            </ValidationGroup>
        </GenericPopup>
    );
};
